import { api, apiFetch } from './client';
import { getSession, setSession } from './session-store';
import type { LoginResponse, MeResponse, OwnExtensionDto, OwnSessionDto } from './types';

type WireMe = Omit<MeResponse, 'mustChangePassword'> & { must_change_password?: boolean; csrf_token?: string };

function toMe(res: WireMe): MeResponse {
  const { csrf_token: _csrf, must_change_password, ...rest } = res;
  void _csrf;
  return { ...rest, mustChangePassword: must_change_password === true };
}

export async function login(email: string, password: string): Promise<MeResponse> {
  const res = await api.post<LoginResponse & WireMe>('/api/v1/auth/login', { email, password });
  const me = toMe(res);
  setSession({ csrfToken: res.csrf_token, me });
  return me;
}

export async function fetchMe(): Promise<MeResponse> {
  // /me also returns the session's CSRF token, so a reloaded tab can keep making writes.
  const res = await api.get<WireMe>('/api/v1/auth/me');
  const me = toMe(res);
  setSession(res.csrf_token ? { csrfToken: res.csrf_token, me } : { me });
  return me;
}

export async function logout(): Promise<void> {
  await api.post('/api/v1/auth/logout');
}

/** Replaces the password; the server ends every other session of the user. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ ended_sessions: number }> {
  const res = await api.post<{ ok: true; ended_sessions: number }>('/api/v1/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  const me = getSession().me;
  if (me) setSession({ me: { ...me, mustChangePassword: false } });
  return res;
}

export function listOwnSessions(): Promise<{ sessions: OwnSessionDto[] }> {
  return api.get('/api/v1/auth/sessions');
}

export function endOwnSession(id: string): Promise<void> {
  return apiFetch<void>(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' });
}

export function listOwnExtensions(): Promise<{ extensions: OwnExtensionDto[] }> {
  return api.get('/api/v1/auth/extensions');
}

export function revokeOwnExtension(id: string): Promise<void> {
  return apiFetch<void>(`/api/v1/auth/extensions/${id}`, { method: 'DELETE' });
}

// ---- first-run setup --------------------------------------------------------

export function fetchSetupStatus(): Promise<{ needs_setup: boolean }> {
  return api.get('/api/v1/setup/status');
}

export interface SetupBody {
  setup_token: string;
  workspace_name: string;
  name: string;
  email: string;
  password: string;
}

export function submitSetup(body: SetupBody): Promise<{ ok: true }> {
  return api.post('/api/v1/setup', body);
}
